/**
 * EntityExplorerModal.tsx
 * Main MUI Dialog shell component
 * Props-based component, no Zustand dependency inside
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import { 
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import type {
  EntityExplorerConfig,
  EntityExplorerResult,
  ImportedVersionResult,
  ExplorerNode,
  ExplorerResultEntityType,
} from '../../../types/entity-explorer.types';
import { useEntityExplorer } from '../../../hooks/useEntityExplorer';
import { ExplorerTable } from './ExplorerTable';
import { ExplorerInfoPanel } from './ExplorerInfoPanel';
import { styles } from './entityExplorer.styles';

/**
 * Props for EntityExplorerModal
 */
export interface EntityExplorerModalProps {
  open: boolean;
  config: EntityExplorerConfig;
  onClose: () => void;
  onConfirm: (result: EntityExplorerResult) => void;
}

/**
 * Main modal component
 */
export function EntityExplorerModal({
  open,
  config,
  onClose,
  onConfirm,
}: EntityExplorerModalProps) {
  // Get all state and navigation functions from hook
  const {
    state,
    navigateTo,
    setPage,
    setLimit,
    setSort,
    setSearch,
  } = useEntityExplorer(config);

  // Local state for selection
  const [singleSelection, setSingleSelection] = useState<ExplorerNode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // When true, the next search-debounce effect run is skipped (folder/entity navigation
  // already cleared search via navigateTo — avoid a racing HOME re-fetch).
  const suppressSearchSyncRef = useRef(false);
  const wasOpenRef = useRef(false);

  // Derived early so effects and handlers can use it
  const isHomeLocation = state.currentNode === 'HOME';

  /**
   * Build breadcrumb array from API breadcrumb trail
   * This uses the full path returned by the backend API
   * Always ensures HOME is the first item so users can navigate back to root
   */
  const getBreadcrumbs = (): Array<{ id: string; name: string; isCurrentLocation: boolean; node?: ExplorerNode }> => {
    // Transform breadcrumbTrail from hook state into displayable breadcrumbs
    let breadcrumbs = state.breadcrumbTrail.map((crumb, index) => ({
      id: crumb._id || 'HOME',
      name: crumb.name,
      isCurrentLocation: index === state.breadcrumbTrail.length - 1,
      node: (crumb as any).node,
    }));

    // Ensure HOME is always first for navigation back to root
    // Check if first item is HOME
    if (breadcrumbs.length === 0 || breadcrumbs[0].id !== 'HOME') {
      breadcrumbs.unshift({
        id: 'HOME',
        name: 'Folders',
        isCurrentLocation: state.currentNode === 'HOME',
        node: {
          id: '',
          name: 'Folders',
          entityType: 'HOME',
          kind: 'folder',
          createdBy: undefined,
          createdAt: undefined,
          raw: {},
        } as ExplorerNode,
      });
    }

    return breadcrumbs;
  };

  /**
   * Debounced server-side search for HOME.
   * For non-HOME nodes all items are loaded at once so we filter locally.
   */
  useEffect(() => {
    if (!isHomeLocation) return;
    if (suppressSearchSyncRef.current) {
      suppressSearchSyncRef.current = false;
      return;
    }
    const timer = setTimeout(() => setSearch(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery, isHomeLocation, setSearch]);

  /**
   * Filter table items by search query.
   * HOME: server handles the filter, return as-is.
   * Non-HOME: all items loaded at once, filter locally.
   */
  const getFilteredItems = () => {
    if (isHomeLocation) return state.children;
    if (!searchQuery.trim()) return state.children;
    return state.children.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  /**
   * Initialize modal only when it transitions from closed → open.
   * Do not re-run when navigateTo identity changes (that was resetting to HOME
   * and cancelling the first folder click).
   */
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      suppressSearchSyncRef.current = true;
      setSearchQuery('');
      navigateTo('HOME', { page: 1, searchTerm: '' });
    }
    wasOpenRef.current = open;
  }, [open, navigateTo]);

  /**
   * Clear info panel when navigating to a different folder/location
   * This ensures version info is only shown while viewing the folder that contains it
   */
  useEffect(() => {
    setSingleSelection(null); // Clear selected version when location changes
  }, [state.currentNode]);

  /**
   * Handle row click (folder, entity, or version)
   * Folders/Entities: Navigate
   * Versions: Select (don't import immediately)
   */
  const handleRowClick = (node: ExplorerNode) => {
    if (node.kind === 'folder' || node.kind === 'entity') {
      // Suppress the HOME search debounce that would fire when clearing searchQuery —
      // that race was overwriting the folder navigation on the first click.
      suppressSearchSyncRef.current = true;
      setSearchQuery('');
      navigateTo(node, { page: 1, searchTerm: '' });
    } else if (node.kind === 'version') {
      // Single select mode: just select and show in info panel
      setSingleSelection(node);
    }
  };

  /**
   * Handle import icon click
   * Confirms import immediately for all versions
   * Note: ModalOutlet will call closeModal() after onConfirm completes
   */
  const handleImportClick = (node: ExplorerNode) => {
    console.log('[EntityExplorerModal] handleImportClick called for node:', node);
    if (node.kind === 'version') {
      // Confirm import immediately
      const resultItem = mapNodeToResult(node);
      console.log('[EntityExplorerModal] Mapped result item:', resultItem);
      onConfirm({
        kind: 'import',
        items: [resultItem],
      });
      // Don't call onClose() here - ModalOutlet handles modal close after callback
    }
  };



  /**
   * Handle Confirm button click
   * Note: ModalOutlet will call closeModal() after onConfirm completes
   */
  const handleConfirm = async () => {
    // Single select: confirm selected version
    if (singleSelection) {
      onConfirm({
        kind: 'import',
        items: [mapNodeToResult(singleSelection)],
      });
      // Don't call onClose() here - ModalOutlet handles modal close after callback
    }
  };

  /**
   * Check if Confirm button should be disabled
   */
  const isConfirmDisabled = (): boolean => {
    return !singleSelection;
  };

  /**
   * Derive modal title from config
   */
  const getModalTitle = (): string => {
    if (config.title) return config.title;
    return 'Choose File';
  };
  const modalTitle = getModalTitle();

  /**
   * Determine what to show in table
   */
  const breadcrumbs = getBreadcrumbs();
  const filteredTableItems = getFilteredItems();
  const tableItems = filteredTableItems;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: '1100px',
              maxWidth: '1100px',
              borderRadius: 0,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              margin: 'auto',
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.3rem',
          fontWeight: 600,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{modalTitle}</span>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#666',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          py: 2,
          px: 3,
          backgroundColor: '#ffffff',
        }}>
          {/* Error message */}
          {state.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {state.error}
            </Alert>
          )}

          {/* Search input - matching Angular style */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" disabled size="small">
                        <SearchIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderColor: '#dee2e6',
                },
              }}
            />
          </Box>

          {/* Breadcrumb navigation - matching Angular style */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 2,
            p: 1.5,
            backgroundColor: '#f4f6fc',
            borderRadius: '8px',
            border: '1px solid #e8eef7',
          }}>
            {breadcrumbs.map((item, index) => (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box 
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  onClick={() => {
                    if (!item.isCurrentLocation) {
                      suppressSearchSyncRef.current = true;
                      setSearchQuery('');
                      if (item.id === 'HOME') {
                        navigateTo('HOME', { page: 1, searchTerm: '' });
                      } else if (item.node) {
                        // Navigate using the node attached to breadcrumb
                        navigateTo(item.node, { page: 1, searchTerm: '' });
                      }
                    }
                  }}
                  style={{
                    cursor: item.isCurrentLocation ? 'default' : 'pointer',
                  }}
                >
                  <span style={{
                    color: item.isCurrentLocation ? '#999999' : '#1976d2',
                    fontSize: '0.9rem',
                    fontWeight: item.isCurrentLocation ? 400 : 500,
                    textDecoration: 'none',
                    cursor: item.isCurrentLocation ? 'default' : 'pointer',
                  }}>
                    {item.name}
                  </span>
                </Box>
                {index < breadcrumbs.length - 1 && (
                  <span style={{ color: '#999', margin: '0 4px' }}>/</span>
                )}
              </Box>
            ))}
          </Box>

          {/* Main content grid (table + info panel) */}
          <Box sx={styles.mainContent as any}>
            {/* Table section */}
            <Box sx={styles.tableContainer as any}>
              {/* Loading state */}
              {state.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : tableItems.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6, 
                  color: '#999',
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1,
                  border: '1px dashed #ddd',
                }}>
                  <p style={{ fontSize: '1.1rem', margin: '8px 0' }}>📭 No items found</p>
                  <p style={{ fontSize: '0.9rem', color: '#bbb', margin: 0 }}>
                    Navigate through folders to find workflows
                  </p>
                </Box>
              ) : (
                <ExplorerTable
                  items={tableItems}
                  config={config}
                  sortField={state.sortField}
                  sortOrder={state.sortOrder}
                  useServerSorting={isHomeLocation}
                  onSortChange={(field, order) => {
                    if (isHomeLocation) {
                      setSort(field, order);
                    }
                  }}
                  onRowClick={handleRowClick}
                  onImportClick={handleImportClick}
                />
              )}

              {isHomeLocation && (
                <TablePagination
                  component="div"
                  count={state.total}
                  page={Math.max(0, state.page - 1)}
                  onPageChange={(_, page) => {
                    setPage(page + 1);
                  }}
                  rowsPerPage={state.limit}
                  onRowsPerPageChange={(event) => {
                    setLimit(parseInt(event.target.value, 10));
                  }}
                  rowsPerPageOptions={[10, 20, 50]}
                />
              )}
            </Box>

            {/* Info panel (right sidebar) */}
            <ExplorerInfoPanel
              node={singleSelection || (state.currentNode === 'HOME' ? null : (state.currentNode as ExplorerNode))}
            />
          </Box>
        </DialogContent>

        {/* Footer with action buttons */}
        <DialogActions sx={{
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          padding: '12px 24px',
          gap: 1,
          justifyContent: 'flex-end',
        }}>
          {config.mode === 'select-folder' && (
            // Select-folder mode: Submit button (disabled at HOME)
            <Button
              onClick={() => {
                if (state.currentNode !== 'HOME' && typeof state.currentNode !== 'string') {
                  // Return properly formatted FolderExplorerResult
                  onConfirm({
                    kind: 'folder',
                    selectedFolder: {
                      _id: state.currentNode.id,
                      name: state.currentNode.name,
                      entityType: state.currentNode.entityType,
                    },
                    folderPath: state.breadcrumbTrail.map(crumb => ({
                      _id: crumb._id,
                      name: crumb.name,
                      entityType: crumb.entityType,
                    })),
                  });
                }
              }}
              variant="contained"
              color="primary"
              disabled={state.currentNode === 'HOME'}
              sx={{ textTransform: 'none', fontSize: '0.9rem' }}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Helper: Map ExplorerNode to ImportedVersionResult
 */
function mapNodeToResult(
  node: ExplorerNode
): ImportedVersionResult {
  const fullName = node.name;
  const preFillName = `${fullName} Copy`;

  // Extract entityId based on entity type
  let entityId: string | undefined;
  const raw = node.raw as any;
  if (raw?.spreadsheetFile?._id) {
    // For SPREADSHEETFILE_VERSION: get the parent spreadsheet id
    entityId = raw.spreadsheetFile._id;
  } else if (raw?.textFile?._id) {
    // For TEXTFILE_VERSION: get the parent text file id
    entityId = raw.textFile._id;
  } else if (raw?.experiment?._id) {
    // For WETLABWORKFLOW/COMPUTATIONALWORKFLOW_VERSION: get the experiment id
    entityId = raw.experiment._id;
  } else if (typeof raw?.experiment === 'string' && raw.experiment) {
    // Some workflow version payloads return experiment as a string id
    entityId = raw.experiment;
  }

  return {
    _id: node.id,
    entityId,
    name: fullName, // Use actual name, not preFillName
    preFillName, // Keep for backward compatibility with workflow copy
    entityType: node.entityType as ExplorerResultEntityType,
    raw: node.raw,
  };
}
